/* import __COLOCATED_TEMPLATE__ from './sales-qualification.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency-decorators';
import { type TaskGenerator } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import { get } from 'embercom/lib/ajax';
import type Session from 'embercom/services/session';
import type User from 'embercom/objects/inbox/user';
import { type NonExpandableSidebarSectionComponentArgs } from 'embercom/lib/inbox2/sidebar-types';
import {
  getOutcomeMemoryFields,
  type MemoryFieldColumn,
} from 'embercom/lib/ai-automation/sdr-memory-fields';

type RoutingOutcome = {
  identifier: string;
  title: string;
  required_memory_fields: Array<{ identifier: string }>;
  optional_memory_fields: Array<{ identifier: string }>;
};

type SdrConfig = {
  app_id: number;
  routing_outcomes: RoutingOutcome[];
};

interface Signature {
  Args: NonExpandableSidebarSectionComponentArgs<{
    user?: User;
  }>;
}

export default class SalesQualification extends Component<Signature> {
  @service declare session: Session;

  @tracked finSdrConfig: SdrConfig | null = null;

  constructor(owner: any, args: Signature['Args']) {
    super(owner, args);
    taskFor(this.loadFinSdrConfig).perform();
  }

  @task({ restartable: true })
  *loadFinSdrConfig(): TaskGenerator<void> {
    try {
      let appId = this.session.workspace.id;
      let response = (yield get(`/teammate_app/fin_sdr_config?app_id=${appId}`)) as SdrConfig;
      this.finSdrConfig = response;
    } catch (error) {
      console.error('Failed to load SDR config:', error);
      this.finSdrConfig = null;
    }
  }

  get qualificationStatus(): string | undefined {
    return this.args.user?.attributes?.fin_qualification_status as string | undefined;
  }

  get isLoadingConfig(): boolean {
    return taskFor(this.loadFinSdrConfig).isRunning;
  }

  get matchedOutcome(): RoutingOutcome | undefined {
    if (!this.finSdrConfig || !this.qualificationStatus) {
      return undefined;
    }
    return this.finSdrConfig.routing_outcomes.find(
      (outcome) => outcome.title === this.qualificationStatus,
    );
  }

  get dynamicFields(): MemoryFieldColumn[] {
    let outcome = this.matchedOutcome;
    if (!outcome) {
      return [];
    }
    return getOutcomeMemoryFields(outcome);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationDetailsSidebar::SidebarSections::SalesQualification': typeof SalesQualification;
    'inbox2/conversation-details-sidebar/sidebar-sections/sales-qualification': typeof SalesQualification;
  }
}
